/**
 * Budget governance — shared money/cap primitives.
 *
 * Browser-safe by construction: no DB, no Node built-ins. Both the API
 * (cost-check, endpoints) and the dashboard UI import from here so the
 * cap math and money formatting can never fork between them.
 *
 * Plan: docs/plans/2026-07-16_BUDGET_GOVERNANCE.md
 *
 * ── Units ──
 * Every amount in this module is **microdollars** (1e-6 USD) as a JS
 * number, matching the canonical DB storage. Display currency is applied
 * only at render time by `formatMoney`; nothing here converts.
 */
import { currencyMinorUnitsPerMajor } from './currency.js'

/**
 * A single monthly cap in microdollars. `null` = unlimited.
 *
 * "Unlimited" still tracks usage — it suppresses the *ceiling*, not the
 * metering. Distinct from `0`, which is a hard block. Any UI must render
 * these as two different states (blank vs a typed zero).
 */
export type BudgetCap = number | null

/**
 * The funding waterfall's bucket set, IN CONSUMPTION ORDER:
 *
 *   promo → free → prepaid → extra → grace
 *
 * - `promo`   drawable promotional credit; drains first so it never burns
 *             included headroom the tenant paid for.
 * - `free`    the included allowance.
 * - `prepaid` usage credits (0141) — the tenant's own prepaid balance. Sits
 *             AFTER `free` on purpose: included credit expires at renewal and
 *             credits never do, so draining the perishable allowance first is
 *             strictly better for the customer.
 * - `extra`   billable/carryover-recovered overage. INERT while every plan
 *             carries `overage_enabled = false` — `subscriptionSource` then
 *             forces `extraCapBillingMicro = 0` and this tier is never selected.
 * - `grace`   the platform-absorbed turn-completion courtesy. Papan's money,
 *             never billed, so `enabled = false` on usage credits does NOT
 *             suppress it.
 *
 * `agent_usage.funding_bucket` also has a DB CHECK pinning this set (0142) —
 * widening the union alone is not enough, the constraint must be widened in a
 * migration or every insert of the new bucket throws.
 *
 * **EVERY consumer that filters or sums by bucket must be checked when this
 * grows.** Two consumers previously handled an unknown bucket by silently
 * relabelling it as `free` (the `resource_usage` sum leg in cost-check, and
 * the orphan sweep's validator) — a money bug, not a type error, because it
 * books someone's paid credit as included allowance. Both now enumerate
 * explicitly; keep it that way.
 */
export type FundingBucket = 'promo' | 'free' | 'prepaid' | 'extra' | 'grace'

/**
 * Storage shape of a cap column: window-keyed, only `month` populated
 * today. Mirrors `BudgetCaps` in the DB schema; day/week are deferred and
 * can be added as keys with no migration.
 */
export interface BudgetCapWindows {
  month?: number | null
}

/** Which cap source is funding the next dollar. */
export type BudgetSource = 'personal' | 'team' | 'role'

/**
 * The fixed draw-down order for read-time attribution: most specific
 * grant depletes first, so a member sees their personal allocation
 * consumed before the team's and the org's.
 *
 * This is display ordering ONLY. Enforcement pools all sources into one
 * ceiling (see `attributeUsage` docs), so changing this array can never
 * change whether a call is allowed.
 */
export const DEFAULT_DRAWDOWN_ORDER: readonly BudgetSource[] = ['personal', 'team', 'role']

/**
 * `DEFAULT_AGENT_CONCURRENCY_CAP` moved to `./entitlement-defaults.js` (0177)
 * and is re-exported below, so every import site keeps working.
 *
 * It moved because it stopped being only the ENFORCER's fallback: the
 * entitlement projector now resolves concurrency to a concrete number using the
 * same constant, and the two agreeing is the point. It sits beside the other
 * three inherited base levels (storage, free, extra) rather than alone in the
 * budget module, so "what does a tenant get when nothing says otherwise" is one
 * answer in one file.
 */
export { DEFAULT_AGENT_CONCURRENCY_CAP } from './entitlement-defaults.js'

export interface AttributionInput {
  personalCap: BudgetCap
  teamCap: BudgetCap
  roleCap: BudgetCap
}

export interface AttributionRow {
  source: BudgetSource
  /** Microdollars drawn from this source under the waterfall. */
  used: number
  /** This source's cap; `null` = unlimited. */
  cap: BudgetCap
  /** True once this source is fully drawn (never true for unlimited). */
  exhausted: boolean
}

export interface AttributionResult {
  rows: AttributionRow[]
  /**
   * The first non-exhausted source in draw order — "where the next dollar
   * comes from". `null` only when every capped source is exhausted and
   * none is unlimited (i.e. the pooled ceiling is spent).
   */
  activeSource: BudgetSource | null
  /**
   * A negative member adjustment, as a positive magnitude, or 0. Rendered
   * as a standalone reduction line ("adjustment −$X") rather than a source
   * row: a negative allocation is not a thing you can draw *from*.
   */
  reduction: number
  /** Pooled ceiling across non-negative sources; `null` if any is unlimited. */
  totalCap: BudgetCap
  /** Echo of the input, for convenience in the UI. */
  totalUsed: number
}

/**
 * Attribute one pooled usage total across its cap sources for display.
 *
 * **This never gates a call.** Enforcement sums personal + team + role
 * into a single ceiling and checks the total, which is precisely why a
 * user is never interrupted when one component is exhausted while others
 * have headroom. This helper reconstructs a per-source *view* of that
 * single number by draining sources in a fixed order — reporting only.
 *
 * Negative personal adjustments (the "disable AI for this user"
 * saturating-negative pattern) are not drawable sources. They surface as
 * `reduction`, applied against the pooled total, and are excluded from
 * the rows and from `activeSource` — otherwise the UI would render a
 * nonsensical "personal $0 / −$9" row.
 */
export function attributeUsage(
  totalUsed: number,
  caps: AttributionInput,
  order: readonly BudgetSource[] = DEFAULT_DRAWDOWN_ORDER,
): AttributionResult {
  const capOf = (s: BudgetSource): BudgetCap =>
    s === 'personal' ? caps.personalCap : s === 'team' ? caps.teamCap : caps.roleCap

  // A negative cap only makes sense for the personal adjustment; treat it
  // as a reduction line and drop it from the waterfall entirely.
  let reduction = 0
  for (const s of order) {
    const c = capOf(s)
    if (c !== null && c < 0) reduction += -c
  }

  let remainingUsed = Math.max(0, totalUsed)
  const rows: AttributionRow[] = []

  for (const source of order) {
    const cap = capOf(source)

    // Negative → reduction line, not a row.
    if (cap !== null && cap < 0) continue

    if (cap === null) {
      // Unlimited absorbs whatever is left and never exhausts.
      rows.push({ source, used: remainingUsed, cap: null, exhausted: false })
      remainingUsed = 0
      continue
    }

    const drawn = Math.min(remainingUsed, cap)
    remainingUsed -= drawn
    // `cap === 0` is a hard block, so it is exhausted from the start —
    // it must never read as a source with headroom for activeSource.
    rows.push({ source, used: drawn, cap, exhausted: drawn >= cap })
  }

  // Overspend (concurrent-call overshoot is accepted by design) lands on
  // the last drawable row so the numbers still sum to totalUsed.
  if (remainingUsed > 0 && rows.length > 0) {
    rows[rows.length - 1].used += remainingUsed
  }

  const activeSource = rows.find(r => !r.exhausted)?.source ?? null

  // Mirror enforcement's additive rule (cost-check `additive`): a net-negative
  // reduction BINDS even against an unlimited component, so a member disabled
  // via a saturating-negative adjustment reads as blocked (0), not unlimited —
  // otherwise the dashboard would contradict the 402 the user actually gets.
  const hasUnlimited = rows.some(r => r.cap === null)
  const net = rows.reduce((sum, r) => sum + (r.cap ?? 0), 0) - reduction
  const totalCap: BudgetCap = hasUnlimited ? (net < 0 ? 0 : null) : Math.max(0, net)

  return { rows, activeSource, reduction, totalCap, totalUsed }
}

/**
 * Resolve the additive pooled ceiling the way enforcement does.
 *
 * Kept here (not just in cost-check) so the dashboard can show the same
 * number the 402 decision uses. Any unlimited component makes the pool
 * unlimited; otherwise components sum and the floor is 0 — a negative
 * adjustment can zero a user out but never produce a negative ceiling.
 */
export function pooledCap(caps: AttributionInput): BudgetCap {
  const parts = [caps.personalCap, caps.teamCap, caps.roleCap]
  // A net-negative reduction binds even against an unlimited component (mirrors
  // cost-check `additive`): a disabled member is 0, not unlimited. Sum the
  // finite parts (negatives are the reduction), then let `null` win only when
  // the net is non-negative.
  const anyUnlimited = parts.some(p => p === null)
  const net = parts.reduce((sum: number, p) => sum + (p ?? 0), 0)
  return anyUnlimited ? (net < 0 ? 0 : null) : Math.max(0, net)
}

/**
 * Per-role default allowance when `tenant_settings.role_caps` carries no
 * override for the role. Per cost.html §Additive cap math: viewer and up
 * unlimited, guest/noaccess zero.
 *
 * THE ONE TABLE (#767). Enforcement (`resolveCaps` in cost-check) and the org
 * Budget editor's fallback for a role with neither an override nor a member
 * holding it both read it, through `resolveRoleCap` below. It lived privately
 * in cost-check while the editor fell back to `null` for every role — so an
 * org with no guest showed Guest as "No ceiling" while guests were enforced
 * at 0. Shown ≠ enforced, in the permissive direction, on a money screen.
 *
 * Owners zeroing themselves needs an explicit override
 * (`role_caps.owner.cap = { month: 0 }`) — the defaults surprise no one.
 */
export const DEFAULT_ROLE_CAPS: Readonly<Record<string, BudgetCap>> = {
  owner: null,
  admin: null,
  member: null,
  viewer: null,
  guest: 0,
  noaccess: 0,
}

/**
 * A role's effective cap: the per-tenant override wins, else the role's
 * default, else 0 for a role the table does not know.
 *
 * `undefined` and `null` are distinct: `undefined` = "no override, fall
 * through"; `null` = an override TO unlimited (the only way to lift guest's 0)
 * and must be preserved — a plain `?? 0` would zero every owner.
 *
 * Unknown roles resolve to 0 — the conservative reading for a membership row
 * whose role the code has not learned yet. `onUnknownRole` lets enforcement log
 * it without this browser-safe module owning a logger. Own-property lookup, so
 * a role named `constructor` is unknown rather than a function.
 */
export function resolveRoleCap(
  override: BudgetCap | undefined,
  role: string,
  onUnknownRole?: (role: string) => void,
): BudgetCap {
  if (override !== undefined) return override
  if (!Object.prototype.hasOwnProperty.call(DEFAULT_ROLE_CAPS, role)) {
    onUnknownRole?.(role)
    return 0
  }
  return DEFAULT_ROLE_CAPS[role]
}

/** Currencies the display layer accepts. Validated at WRITE time, because
 *  an arbitrary string throws inside Intl.NumberFormat at RENDER time —
 *  i.e. in the user's dashboard, far from the admin who typed it. */
export const SUPPORTED_DISPLAY_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'MYR', 'SGD', 'AUD', 'CAD', 'INR',
] as const

export type DisplayCurrency = typeof SUPPORTED_DISPLAY_CURRENCIES[number]

export function isSupportedDisplayCurrency(v: unknown): v is DisplayCurrency {
  return typeof v === 'string' && (SUPPORTED_DISPLAY_CURRENCIES as readonly string[]).includes(v)
}

/**
 * Where a resolved display rate came from. Surfaced to the platform-admin
 * editor so an operator can tell a rate they pinned from one the deployment
 * derived for them — the two states look identical as a number and mean very
 * different things when the credit rate next moves.
 */
export type DisplayRateSource = 'explicit' | 'derived' | 'env' | 'canonical'

export interface DisplayCurrencyInputs {
  /** `tenant_settings.display_currency` — `null` when the tenant has no row. */
  tenantCurrency?: string | null
  /** `tenant_settings.display_currency_rate`. `null` = DERIVE (migration 0154);
   *  arrives as a string because `numeric(18,6)` round-trips as one in pg. */
  tenantRate?: string | number | null
  /** `billing_settings.currency` — what the deployment actually charges in. */
  billingCurrency?: string | null
  /** `billing_settings.credit_rate_minor_per_usd`: MINOR units of
   *  `billingCurrency` per US$1.00 of credit. */
  creditRateMinorPerUsd?: number | null
  /** `PAPAN_DEFAULT_DISPLAY_CURRENCY` / `PAPAN_DEFAULT_DISPLAY_RATE`. */
  envCurrency?: string | null
  envRate?: string | number | null
}

export interface ResolvedDisplayCurrency {
  displayCurrency: string
  displayCurrencyRate: number
  displayCurrencyRateSource: DisplayRateSource
}

/** A rate that can actually be rendered: finite and strictly positive. A `0`
 *  would make a spending screen show no spending. */
function usableRate(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * The ONE resolution chain for "what currency, at what multiplier, does this
 * tenant's spend render in".
 *
 *   explicit tenant rate → derived credit rate → env default → USD @ 1
 *
 * ── Why the rate is DERIVED rather than typed (2026-08-03) ────────────
 * For a prepaid-credit business the display rate is not an FX question. A
 * customer pays `credit_rate_minor_per_usd` of the billing currency per US$1.00
 * of credit — RM5.50/USD by default. Rendering their spend at an interbank rate
 * reads 15–24% below what left their bank account. Deriving from the credit rate
 * means the screen can never drift from the receipt, and there is nothing left
 * to keep current — which is why this design deletes the staleness-warning idea
 * outright rather than deferring it. If you find yourself wanting a "rate may be
 * stale" banner, the rate has stopped being derived and THAT is the bug.
 *
 * Three rules that are easy to get backwards:
 *
 *  1. **USD is canonical and never carries a multiplier.** Storage is USD
 *     microdollars, so a USD rate of anything but 1 renders every dollar figure
 *     at the wrong amount. Short-circuited here, not just refused at the write
 *     route — after 0154 a brand-new `tenant_settings` row (written by any of
 *     the sibling budget setters) carries the column default `'USD'` with a NULL
 *     rate, and deriving 5.50 onto it would render US$100 as "$550".
 *  2. **The credit rate is only meaningful in the BILLING currency.** It is
 *     minor units of `billing_settings.currency`; deriving RM5.50 onto a tenant
 *     displaying SGD would be a fabricated quote. Mismatch falls through.
 *  3. **Minor → major uses the currency's own exponent, never `/100`.** A
 *     hardcoded `/100` is a 100× error on JPY (zero decimals) and 10× on BHD
 *     (three) — the same rule `formatCurrencyMinor` exists for.
 *
 * The terminal fallback is USD @ 1 rather than "the tenant's currency at 1":
 * labelling a USD-magnitude number as MYR is a wrong amount, where canonical USD
 * is merely not the label the operator picked.
 *
 * Pure and browser-safe — every input is passed in, nothing is read here.
 */
export function resolveDisplayCurrency(
  inputs: DisplayCurrencyInputs,
): ResolvedDisplayCurrency {
  const envRate = usableRate(inputs.envRate)
  const env = isSupportedDisplayCurrency(inputs.envCurrency) && envRate !== null
    ? { currency: inputs.envCurrency as string, rate: envRate }
    : null

  const tenantCurrency = isSupportedDisplayCurrency(inputs.tenantCurrency)
    ? inputs.tenantCurrency
    : null
  const currency = tenantCurrency ?? env?.currency ?? 'USD'

  // Rule 1 — canonical.
  if (currency === 'USD') {
    return { displayCurrency: 'USD', displayCurrencyRate: 1, displayCurrencyRateSource: 'canonical' }
  }

  const explicit = usableRate(inputs.tenantRate)
  if (explicit !== null) {
    return { displayCurrency: currency, displayCurrencyRate: explicit, displayCurrencyRateSource: 'explicit' }
  }

  const derived = deriveDisplayRate(inputs.billingCurrency, inputs.creditRateMinorPerUsd, currency)
  if (derived !== null) {
    return { displayCurrency: currency, displayCurrencyRate: derived, displayCurrencyRateSource: 'derived' }
  }

  if (env && env.currency === currency) {
    return { displayCurrency: currency, displayCurrencyRate: env.rate, displayCurrencyRateSource: 'env' }
  }

  return { displayCurrency: 'USD', displayCurrencyRate: 1, displayCurrencyRateSource: 'canonical' }
}

/**
 * `credit_rate_minor_per_usd` → a display multiplier, or `null` when it cannot
 * honestly produce one for `displayCurrency` (rule 2 above).
 *
 * Exported because the platform-admin editor shows the operator the value they
 * would get by clearing their override, and the API row carries it alongside
 * the resolved rate for exactly that preview.
 */
export function deriveDisplayRate(
  billingCurrency: string | null | undefined,
  creditRateMinorPerUsd: number | null | undefined,
  displayCurrency: string,
): number | null {
  if (!billingCurrency) return null
  if (billingCurrency.toUpperCase() !== displayCurrency.toUpperCase()) return null
  if (typeof creditRateMinorPerUsd !== 'number' || !Number.isFinite(creditRateMinorPerUsd)) return null
  if (creditRateMinorPerUsd <= 0) return null
  // Rule 3 — the currency's own exponent, not a hardcoded 100.
  const rate = creditRateMinorPerUsd / currencyMinorUnitsPerMajor(billingCurrency)
  return Number.isFinite(rate) && rate > 0 ? rate : null
}

export interface MoneyFormatOptions {
  /** ISO-4217 code. Defaults to USD. */
  currency?: string
  /** Multiplier applied to the USD amount. Display only — never FX. */
  rate?: number
  /** BCP-47 locale for grouping/symbol placement. */
  locale?: string
  /** Force fraction digits; defaults to 2, or 4 for sub-cent amounts. */
  maximumFractionDigits?: number
}

/**
 * Render microdollars as a currency string.
 *
 * Non-USD output is APPROXIMATE: the rate is an admin-set constant, not a
 * live feed, so it goes stale. Call sites showing a non-USD figure must
 * label it as such (the plan's Phase 5 tooltip requirement) — unlabeled
 * converted money is a misleading-numbers problem, not a cosmetic one.
 */
export function formatMoney(microdollars: number, opts: MoneyFormatOptions = {}): string {
  const { currency = 'USD', rate = 1, locale = 'en-US' } = opts

  // ── P7 / billing-native model ────────────────────────────────────────
  //
  // `microdollars` is now BILLING-CURRENCY micro on every money path: caps,
  // balances and usage totals were re-denominated, so the amount arriving here
  // is already in the tenant's own currency.
  //
  // That makes `rate` a DOUBLE conversion. It survives as a parameter only for
  // the display-currency layer, which M4 parks and §7 retires — so a non-1
  // rate is now a defect, not a feature, and it is the silent kind: the number
  // stays plausible and is wrong by the whole credit rate.
  //
  // Callers must pass the deployment's `billing_currency` for the SYMBOL.
  // The 'USD' default is left in place deliberately rather than changed to a
  // literal: this module is dependency-free and cannot resolve a deployment's
  // currency, so a wrong default here would be as wrong as the one it
  // replaced. A DTO that omits the currency is the bug.
  // NOT a throw, deliberately. The display layer is still wired into
  // notification and promo copy (four suites exercise it), and §7 retires that
  // machinery in a follow-up rather than in Gate 1. Throwing here would break
  // live callers to prevent a defect none of them has yet. Retire the layer,
  // then make this an error.
  const amount = (microdollars / 1_000_000) * rate

  // Sub-cent amounts are common (a single cheap LLM call), and rounding
  // them to $0.00 makes a populated usage table look empty.
  const maximumFractionDigits =
    opts.maximumFractionDigits ?? (amount !== 0 && Math.abs(amount) < 0.01 ? 4 : 2)

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits,
      minimumFractionDigits: Math.min(2, maximumFractionDigits),
    }).format(amount)
  } catch {
    // Unknown currency code slipped past write-time validation (legacy
    // row, hand-edited DB). Degrade to a readable amount rather than
    // throwing inside a render.
    return `${amount.toFixed(maximumFractionDigits)} ${currency}`
  }
}

/**
 * An accrued-spend figure, in BOTH the canonical unit and the rendered one.
 *
 * Introduced with rate history (0172/0173). A period may span more than one
 * rate, so there is no single multiplier a client could apply that is correct
 * for every figure in it — a weighted average is exact for the total and wrong
 * for each member, feature and model beneath it. The server therefore converts
 * per row and sends the result.
 *
 * `displayMinor === null` means CANONICAL USD: nothing was converted, render
 * `usd` through `formatMoney`. It is not `0`, which is a real amount — the same
 * `null` ≠ `0` discipline the caps carry.
 *
 * Only ACCRUED SPEND uses this shape. Caps, ceilings, balances and thresholds
 * are point-in-time, convert at today's rate, and stay single-valued.
 */
export interface MoneyFigure {
  /** Canonical USD microdollars — what enforcement compares against caps. */
  usd: number
  /** Display-currency MINOR units, or `null` when the figure is canonical USD. */
  displayMinor: number | null
}

/** Render a cap, preserving the unlimited/zero distinction. */
export function formatCap(cap: BudgetCap, opts: MoneyFormatOptions = {}): string {
  return cap === null ? 'Unlimited' : formatMoney(cap, opts)
}

/**
 * Is this figure a CONVERTED approximation rather than a canonical amount?
 *
 * Canonical storage is USD microdollars. A non-USD display currency is
 * produced by multiplying by `display_currency_rate`, a constant an admin
 * typed in — not a live FX feed — so it drifts from reality the moment
 * rates move.
 *
 * Any surface showing a converted figure MUST mark it as approximate
 * (see `APPROXIMATE_CURRENCY_NOTE`). Unlabeled converted money reads as
 * an exact amount, which in a *spending* UI is a misleading-numbers
 * problem, not a cosmetic one — and it costs one tooltip to avoid.
 *
 * The rate is checked too: a tenant left on USD at rate 1 is canonical,
 * but a nonsensical USD-at-0.9 configuration is still an approximation.
 */
export function isApproximateCurrency(currency?: string, rate?: number): boolean {
  return (currency ?? 'USD') !== 'USD' || (rate ?? 1) !== 1
}

/** The disclosure to attach wherever a converted figure appears. Kept as
 *  one constant so the wording can't drift between the dashboard, the
 *  bell and the email. */
export const APPROXIMATE_CURRENCY_NOTE =
  'Converted at the rate in force when the spend was incurred; canonical amounts are in USD.'

/** Short inline marker for space-constrained surfaces (table cells,
 *  notification bodies) where the full note doesn't fit. */
export const APPROXIMATE_CURRENCY_MARKER = '≈'

/**
 * Format money, prefixing `≈` when the figure is a converted
 * approximation. Prefer this over bare `formatMoney` on any user-facing
 * surface; use `formatMoney` directly only where the currency is known
 * to be canonical USD.
 */
export function formatMoneyLabeled(microdollars: number, opts: MoneyFormatOptions = {}): string {
  const formatted = formatMoney(microdollars, opts)
  return isApproximateCurrency(opts.currency, opts.rate)
    ? `${APPROXIMATE_CURRENCY_MARKER}${formatted}`
    : formatted
}

/**
 * Read a window-keyed cap column, preferring the JSONB `month` value and
 * falling back to the legacy int4 scalar (expand-style read).
 *
 * `undefined`/absent `month` falls through to the scalar; an explicit
 * `null` month means the caller deliberately cleared the cap, so it wins
 * over a stale scalar rather than resurrecting it.
 *
 * NOTE: this is for the tenant/role axes, where absent = unlimited. Team
 * caps and member adjustments are additive CONTRIBUTIONS where absent
 * means 0 — those are coalesced inside their SQL aggregate, not here.
 *
 * ── Why the result is coerced to a Number ───────────────────────────────
 *
 * `free_cap_usd` / `extra_cap_usd` are **bigint** columns, and node-postgres
 * returns int8 as a STRING — so every caller reading them off the raw pool
 * (rather than through drizzle's `mode: 'number'`) gets `"0"`, not `0`. That is
 * type-correct and silently catastrophic the moment anything ADDS the two axes:
 * `120000000 + "0"` is the string `"1200000000"`, and a customer whose ceiling
 * is $120 renders as $1,200 — ten times the truth, with no error anywhere.
 *
 * Found in the browser on the first tenant provisioned from the console (phase
 * 6), which is the combination that makes it reachable: entitlements write a
 * real `extra_cap_usd = 0` while the operator's own ceiling lands in the JSONB.
 * It hit BOTH raw-pool readers — the customer header's `capState` and the
 * Overview's over-cap count.
 *
 * Coerced HERE rather than at each call site because this function is already
 * the one designated reader of the pair; a per-caller `Number()` is a thing a
 * new caller can forget, and the failure is invisible. `null` short-circuits
 * before the coercion — `Number(null)` is `0`, which would turn "unlimited"
 * into "hard block", the single worst direction to be wrong in.
 */
export function readMonthCap(
  jsonb: BudgetCapWindows | null | undefined,
  scalar: number | string | null | undefined,
): BudgetCap {
  if (jsonb && 'month' in jsonb) return jsonb.month ?? null
  if (scalar == null) return null
  const n = Number(scalar)
  // A non-numeric string is a corrupt row, not a cap. Reading it as NaN would
  // make every comparison false, i.e. silently unlimited.
  return Number.isFinite(n) ? n : null
}

/** ───── Exhaustion copy selection (usage credits, C4) ──────────────
 *
 * With overage dropped, running out of AI budget is a HARD STOP, so the 402
 * has to tell the specific person what THEY can do about it — a member can
 * only ask an admin; an admin can buy credits, or wait for the renewal; an
 * admin who switched credits off needs to know that's why.
 *
 * The branch lives HERE, in the dependency-free package, because it is read
 * from both sides of a layering boundary:
 *
 *   - the API (`buildFailure` in cost-check.ts) picks the ENGLISH sentence it
 *     puts in the 402 body, which is what SDK/API callers and any non-SPA
 *     client see;
 *   - the SPA (`aguiErrorMessage`) picks an i18n KEY for the same row, so a
 *     Malay user reads Malay rather than the server's English.
 *
 * Two hand-kept copies of a four-way branch is precisely the drift that
 * produced the two silent bucket-relabel bugs documented on `FundingBucket`.
 * One selector, two renderers.
 */

/** The subset of a 402 `breakdown` this decision needs. Structural, not an
 *  import of `CapCheckFail` — that type lives in features/agent/api, which
 *  neither this package nor the SPA may depend on. */
export interface ExhaustionContext {
  /** The `CapErrorCode` from the 402 body. Free text: an unknown/legacy code
   *  must fall through to `server_message`, never throw. */
  code?: string
  /** Masked (`null`) on owner-pays app turns — see `maskRemainders`. */
  creditsEnabled?: boolean | null
  creditBalanceBillingMicro?: number | null
  /** ISO timestamp when the tenant's included pool refills. Under the
   *  two-layer model this is the SUBSCRIPTION renewal date for a paying
   *  tenant, not the 1st of the month. */
  poolRenewsAt?: string | null
  /** Whether the VIEWER can act (owner/admin of the paying tenant). False for
   *  a plain member, and false on owner-pays turns where the viewer isn't a
   *  member of the payer tenant at all. */
  viewerCanManageBilling?: boolean
  /**
   * Did a DIFFERENT tenant fund this turn than the one being worked in (0180)?
   *
   * True for a platform support seat: every other field here describes the
   * PLATFORM org — its balance, its renewal date, and a role held there — while
   * the person is looking at a customer's workspace. Left unset the copy reads
   * "Your organization is out of AI credit" with the platform's renewal date
   * and a Buy credits button that opens the CUSTOMER's billing page, where a
   * purchase cannot unblock the turn. Indistinguishable from the customer being
   * out of money, and actionable in the wrong direction.
   */
  payerIsElsewhere?: boolean
}

/**
 * Which of the §7 rows to render.
 *
 * `server_message` is the catch-all: dunning, disputes, the app ceiling, grace
 * gates and anything added later all carry their own written-for-humans
 * sentence already, and inventing a client-side copy for them would go stale
 * the moment the server's wording changes. Only the two credit-exhaustion
 * codes are localized here.
 */
export type ExhaustionCopyKey =
  /** The member's own allocation is gone; the org may still be funded. */
  | 'user_quota'
  /** Org funding gone, viewer is a plain member — they can only escalate. */
  | 'tenant_member'
  /** Org funding gone, viewer is owner/admin — buy credits or wait for renewal. */
  | 'tenant_admin'
  /** Org funding gone AND an admin switched credits off — say so, because the
   *  fix is a toggle, not a purchase. */
  | 'tenant_admin_credits_off'
  /** Not a budget-exhaustion refusal: show the server's own message verbatim. */
  | 'server_message'
  /** The turn was funded by ANOTHER tenant (a platform support seat) and that
   *  wallet is out. Nothing the viewer does in THIS organization helps, and
   *  the organization's own budget is untouched — so this says so and offers
   *  no purchase here. */
  | 'payer_elsewhere'

export function selectExhaustionCopy(ctx: ExhaustionContext): ExhaustionCopyKey {
  if (ctx.code === 'COST_LIMIT_USER') return 'user_quota'
  // Before every tenant branch: each of those describes "your organization",
  // which is the wrong organization when someone else funded the turn.
  if (ctx.code === 'COST_LIMIT_TENANT' && ctx.payerIsElsewhere) return 'payer_elsewhere'
  if (ctx.code !== 'COST_LIMIT_TENANT') return 'server_message'
  if (!ctx.viewerCanManageBilling) return 'tenant_member'
  // `enabled = false` is only the STORY when there is money it's holding back.
  // A tenant with credits off and a zero balance is just out of credit, and
  // telling them to flip a switch that would reveal an empty balance is a
  // dead end. `null` is the masked case — never claim it's switched off.
  if (ctx.creditsEnabled === false && (ctx.creditBalanceBillingMicro ?? 0) > 0) {
    return 'tenant_admin_credits_off'
  }
  return 'tenant_admin'
}
